// ---------------------------------------------------------------------------
// PWA Caisse Tablette — useQrScanner hook
// Wraps the qr-scanner npm package with React lifecycle and
// provides barcode/QR code parsing logic (RESA:, CLIENT:, TABLE:, etc.).
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import QrScanner from 'qr-scanner';
import { QR_SCAN_DEBOUNCE } from '@/lib/constants';
import type { ScanResult, ScanType } from '@/lib/types';

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

interface UseQrScannerReturn {
  /** Start the camera and begin scanning. */
  startScanning: () => Promise<void>;
  /** Stop the camera and scanning. */
  stopScanning: () => void;
  /** Whether the scanner is currently active. */
  isScanning: boolean;
  /** Parse a scanned code string into a typed ScanResult. */
  parseScannedCode: (code: string) => ScanResult;
  /** Toggle the flashlight/torch. */
  toggleTorch: () => Promise<void>;
  /** Switch between front and back cameras. */
  switchCamera: () => Promise<void>;
  /** Whether the torch is currently on. */
  torchOn: boolean;
}

// ---------------------------------------------------------------------------
// Parse logic
// ---------------------------------------------------------------------------

/**
 * Parse a raw scanned code string into a typed ScanResult.
 * Detects reservation, client, ticket, table, product barcode, JSON, or unknown.
 */
export function parseScannedCode(code: string): ScanResult {
  // RESA:{id}
  if (code.startsWith('RESA:')) {
    return { type: 'reservation', value: code.substring(5) };
  }

  // CLIENT:{id}
  if (code.startsWith('CLIENT:')) {
    return { type: 'client', value: code.substring(7) };
  }

  // TICKET:{id}
  if (code.startsWith('TICKET:')) {
    return { type: 'ticket', value: code.substring(7) };
  }

  // TABLE:{id}
  if (code.startsWith('TABLE:')) {
    return { type: 'table', value: code.substring(6) };
  }

  // Product barcode (EAN-8, EAN-13, etc.)
  if (/^\d{8,13}$/.test(code)) {
    return { type: 'product', value: code };
  }

  // JSON QR code
  try {
    const parsed = JSON.parse(code);
    return { type: 'json', value: code, data: parsed };
  } catch {
    // Not JSON
  }

  // Unknown
  return { type: 'unknown', value: code };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Create and manage a QR/barcode scanner bound to a `<video>` element.
 *
 * @param videoRef - React ref attached to the `<video>` element.
 * @param onScan - Callback invoked with a parsed ScanResult when a code is scanned.
 */
export function useQrScanner(
  videoRef: RefObject<HTMLVideoElement | null>,
  onScan: (result: ScanResult) => void,
): UseQrScannerReturn {
  const scannerRef = useRef<QrScanner | null>(null);
  const lastScanRef = useRef<number>(0);
  const [isScanning, setIsScanning] = useState(false);
  const [torchOn, setTorchOn] = useState(false);

  // Stabilise the callback ref so the scanner doesn't need recreation
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  // ---- create scanner on mount --------------------------------------------

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const scanner = new QrScanner(
      video,
      (result) => {
        const now = Date.now();
        if (now - lastScanRef.current < QR_SCAN_DEBOUNCE) return;
        lastScanRef.current = now;

        const data = result.data;

        // Vibration feedback
        if (navigator.vibrate) {
          navigator.vibrate(200);
        }

        // Parse and dispatch
        const parsed = parseScannedCode(data);
        onScanRef.current(parsed);
      },
      {
        preferredCamera: 'environment',
        highlightScanRegion: true,
        highlightCodeOutline: true,
        maxScansPerSecond: 5,
      },
    );

    scannerRef.current = scanner;

    return () => {
      scanner.destroy();
      scannerRef.current = null;
      setIsScanning(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef]);

  // ---- public controls ----------------------------------------------------

  const startScanning = useCallback(async () => {
    if (!scannerRef.current) return;
    await scannerRef.current.start();
    setIsScanning(true);
  }, []);

  const stopScanning = useCallback(() => {
    if (!scannerRef.current) return;
    scannerRef.current.stop();
    setIsScanning(false);
    setTorchOn(false);
  }, []);

  const toggleTorch = useCallback(async () => {
    if (!scannerRef.current) return;
    try {
      await scannerRef.current.toggleFlash();
      setTorchOn((prev) => !prev);
    } catch {
      // Flash not supported on this device
    }
  }, []);

  const switchCamera = useCallback(async () => {
    if (!scannerRef.current) return;
    try {
      // Cycle through available cameras
      const cameras = await QrScanner.listCameras(true);
      if (cameras.length <= 1) return;
      // The library handles cycling internally
      await scannerRef.current.stop();
      await scannerRef.current.start();
    } catch {
      // Camera switch not supported
    }
  }, []);

  return {
    startScanning,
    stopScanning,
    isScanning,
    parseScannedCode,
    toggleTorch,
    switchCamera,
    torchOn,
  };
}
